/**
 * feat-241 — ONE fetch shared by the matrix and the passport (P12, AC18).
 *
 * This is a LAYOUT ROUTE, and that is load-bearing rather than tidiness. Both
 * `/restaurant/curriculum-coverage` and
 * `/restaurant/curriculum-coverage/:serverId` are children of it, so navigating
 * from a matrix row into that person's passport keeps this provider MOUNTED and
 * the passport renders from the response the matrix already has: no second
 * network call, and no second evaluator path where "is this track locked" could
 * be answered differently.
 *
 * Landing on the passport URL directly (P5 — it is shareable, and the back button
 * works) mounts the provider fresh and fetches ONCE. That is the first call, not
 * a second one.
 *
 * The location filter lives here rather than on the page, because it is an input
 * to the fetch: the selection is forwarded to the endpoint as `location_id` so
 * the BACKEND re-scopes the roster. Never a client-side row hide — a store
 * manager is pinned server-side and fail-closed to an empty roster, and hiding
 * rows in the client would make the two disagree.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { config } from '../../../config/environment';
import managementService from '../../../services/managementService';
import {
  CurriculumCoverageForbiddenError,
  getCurriculumCoverage,
  type CurriculumCoverageResponse,
} from '../../../services/curriculumCoverageService';

export interface CoverageLocation {
  id: string;
  name: string;
}

interface CoverageContextValue {
  data: CurriculumCoverageResponse | null;
  loading: boolean;
  error: string | null;
  forbidden: boolean;
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string | null) => void;
  availableLocations: CoverageLocation[];
  canFilterByLocation: boolean;
  /**
   * Re-runs the fetch (design F5). Lives here rather than on the page because
   * the provider owns the single request — a page-local retry would have to
   * duplicate the call and would then be a SECOND fetch path, which is the exact
   * divergence P12 exists to prevent.
   */
  retry: () => void;
}

const CoverageContext = createContext<CoverageContextValue | null>(null);

export function useCurriculumCoverage(): CoverageContextValue {
  const ctx = useContext(CoverageContext);
  if (!ctx) {
    throw new Error('useCurriculumCoverage must be used inside CurriculumCoverageProvider');
  }
  return ctx;
}

const FAKE = config.environment === 'fake';

/** Mirrors the fake roster's two real locations in curriculumCoverageService. */
const FAKE_LOCATIONS: CoverageLocation[] = [
  { id: 'loc-sf', name: 'Ladle & Leaf SF' },
  { id: 'loc-oak', name: 'Ladle & Leaf Oakland' },
];

export const CurriculumCoverageProvider: React.FC = () => {
  const { user } = useAuth();
  const { selectedTenant } = useTenant();

  const [data, setData] = useState<CurriculumCoverageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [availableLocations, setAvailableLocations] = useState<CoverageLocation[]>([]);
  // Bumped by retry() to re-run the fetch effect. A counter rather than a
  // callback so the effect stays the ONE place the request is issued.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Corporate is treated identically to Admin for "can filter freely" — the
  // standing RBAC convention in this app, not a new rule.
  const normalizedRole = useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return null;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);
  const isAdminOrCorporate = normalizedRole === 'Admin' || normalizedRole === 'Corporate';
  // Fake mode has no real role; show the filter so it is demonstrable in start:fake.
  const canFilterByLocation = FAKE || isAdminOrCorporate;

  // Reset the selection when the tenant changes — its locations differ.
  useEffect(() => {
    setSelectedLocationId(null);
  }, [selectedTenant]);

  useEffect(() => {
    if (FAKE) {
      setAvailableLocations(FAKE_LOCATIONS);
      return;
    }
    if (!isAdminOrCorporate) return;
    const tenantId = selectedTenant || user?.managementUser?.tenant_id;
    if (!tenantId) return;
    let cancelled = false;
    managementService
      .getLocationsByTenant(tenantId)
      .then((resp: any) => {
        if (cancelled) return;
        if (resp.success && resp.locations) {
          setAvailableLocations(
            resp.locations.map((l: any) => ({ id: l.id, name: l.name })),
          );
        }
      })
      // Non-blocking: a missing list just leaves "All locations".
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdminOrCorporate, selectedTenant, user?.managementUser?.tenant_id]);

  useEffect(() => {
    const tenantId = selectedTenant || user?.managementUser?.tenant_id;
    if (!tenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setForbidden(false);
    getCurriculumCoverage(tenantId, selectedLocationId ? { locationId: selectedLocationId } : {})
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof CurriculumCoverageForbiddenError) setForbidden(true);
        else setError('Could not load curriculum coverage.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenant, selectedLocationId, user?.managementUser?.tenant_id, reloadNonce]);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      forbidden,
      selectedLocationId,
      setSelectedLocationId,
      availableLocations,
      canFilterByLocation,
      retry: () => setReloadNonce((n) => n + 1),
    }),
    [data, loading, error, forbidden, selectedLocationId, availableLocations, canFilterByLocation],
  );

  return (
    <CoverageContext.Provider value={value}>
      <Outlet />
    </CoverageContext.Provider>
  );
};

export default CurriculumCoverageProvider;
